// =============================================================================
// SIREPPAR - Layout Principal
// =============================================================================

import { useState, useEffect, Fragment } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Transition, Menu } from '@headlessui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebarState } from '../../hooks/useSidebarState';
import { useReconocimientoApoderado } from '../../hooks/useReconocimientoApoderado';
import { useAlianzaPartido } from '../../hooks/useAlianzaPartido';
import { useAgrupaciones } from '../../hooks/useAgrupaciones';
import { CONSULTAS_HABILITADAS, ROLES_CONSULTAS_PARTIDO } from '../../utils/consultas';
import { useConsultasPendientes } from '../../hooks/useConsultasPendientes';
import { useConsultasPendientesPartido } from '../../hooks/useConsultasPendientesPartido';
import SidebarGroup from '../SidebarGroup';
import CampanaAlertas from '../dashboard/CampanaAlertas';

const ROLES_INTERNOS = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'MESA_ENTRADAS', 'ANALISTA', 'AUDITOR'];

// Ítem único de Interacciones y Consultas de partidos. Sin consultas habilitadas
// (producción) solo hay interacciones, y el nombre no promete lo que no está.
const ETIQUETA_COMUNICACIONES = CONSULTAS_HABILITADAS ? 'Consultas' : 'Interacciones';
// Lleva a la bandeja de consultas. Sin consultas habilitadas, /gestion-consultas
// redirige al dashboard (App.tsx), así que ahí sigue llevando a Interacciones.
const DESTINO_COMUNICACIONES = CONSULTAS_HABILITADAS ? '/gestion-consultas' : '/interacciones';
const AUTENTICIDAD_HABILITADA = import.meta.env.VITE_AUTENTICIDAD_HABILITADA === 'true';
// Secciones visibles para el apoderado de un reconocimiento en formación, según el momento:
// - mientras carga la documentación (wizard pendiente): solo Inicio + Usuarios (foco en el wizard).
// - documentación ya enviada: se suma Autoridades (para ver las promotoras cargadas).
// - resto de la formación (etapa de atributos/admisibilidad): Inicio, Expedientes, Usuarios.
//   (Documentos NO: el partido aún no puede subir documentación en admisibilidad.)
const TABS_RECON_WIZARD_PENDIENTE = ['/dashboard', '/usuarios', '/consultas'];
const TABS_RECON_DOC_ENVIADA = ['/dashboard', '/expedientes', '/documentos', '/autoridades', '/usuarios', '/consultas'];
const TABS_RECON_EN_FORMACION = ['/dashboard', '/expedientes', '/usuarios', '/consultas'];
// Etapa de afiliados del provisorio (4 meses para reunir el 4‰): el foco es
// cargar afiliados, así que se deja Inicio + Afiliados + Usuarios, más
// Expedientes y Documentos para poder seguir el trámite y ver lo presentado.
const TABS_RECON_AFILIADOS = ['/dashboard', '/afiliados', '/expedientes', '/documentos', '/usuarios', '/consultas'];
// Etapa de autoridades definitivas: el foco es cargar las autoridades electas, así
// que se deja Inicio + Autoridades (para ver la nómina) + Expedientes y Documentos
// para seguir el trámite y lo presentado.
const TABS_RECON_AUTORIDADES = ['/dashboard', '/autoridades', '/expedientes', '/documentos', '/usuarios', '/consultas'];

// Secciones que el partido en trámite de reconocimiento recién ve cuando queda
// VIGENTE (reconocimiento definitivo).
const SECCIONES_SOLO_VIGENTE = ['/mi-partido', '/financiamiento'];

// Financiamiento se oculta con una condición MÁS AMPLIA: también en
// reempadronamiento mientras el partido no esté VIGENTE. Un partido en trámite no
// tiene aporte público ni rendición que presentar. Ver `restringirFinanciamiento`
// en useReconocimientoApoderado.
const SECCIONES_SOLO_VIGENTE_FINANCIAMIENTO = ['/financiamiento'];

// Usuario DE una alianza (su `partidoId` es la alianza). No gestiona un partido:
// una alianza no tiene afiliados, ni autoridades a cargar en /autoridades, ni
// rendiciones de financiamiento — todo eso es de cada partido integrante. Su
// portal es el trámite y nada más.
//
// La restricción NO depende del estado del trámite: aunque la alianza quede
// reconocida, esas secciones siguen sin aplicarle. Las candidaturas, que sí son de
// la alianza, se habilitan en su propia card.
//
// Al apoderado de un partido INTEGRANTE no se le toca la navegación: sigue
// gestionando su propio partido con normalidad, y de la alianza sólo ve el banner
// del inicio.
//
// /usuarios va SIEMPRE, en cualquier etapa del trámite. Antes quedaba afuera por
// miedo a que una cuenta creada desde el wizard público diera de alta usuarios sin
// autorización, pero el alta no es libre: `exigirEnNomina` sólo acepta DNIs que la
// agrupación declaró como apoderados o autoridades SUYOS, y el rol se limita a los
// tres de firma. Sin la sección, el único apoderado con acceso no tenía forma de
// darle entrada a su co-apoderado — que es el caso normal, no la excepción.
const TABS_ALIANZA = ['/dashboard', '/expedientes', '/usuarios', '/consultas'];

// Candidaturas se le abre a la alianza en cuanto la Secretaría Electoral la marca
// habilitada para la convocatoria (AgrupacionHabilitadaConvocatoria.habilitada),
// sin esperar a que quede VIGENTE (reconocida por el TEP): si el expediente de
// constitución se demora, la alianza igual puede necesitar presentar su
// candidato antes del cierre de candidaturas. La condición real es el flag
// `habilitada`, que ya decide un humano de la Secretaría (ver
// convocatoriaService.ts, actualizarHabilitacion).
//
// Y le corresponde a la ALIANZA, no a sus integrantes: cuando un partido va en
// alianza, la lista la presenta la alianza. Que la sesión del usuario de la alianza
// tenga `partidoId` = la alianza es lo que hace que `/candidaturas/mi-estado`
// —que deriva la agrupación del usuario— responda por ella sin cambios.
//
// ESTA LISTA TIENE QUE INCLUIR LAS RUTAS, NO SÓLO LAS SECCIONES DEL MENÚ.
// ------------------------------------------------------------------------------
// Cumple dos funciones a la vez, y es fácil acordarse de una sola:
//   1. filtra los ítems del menú lateral (`tabsPermitidasExternas`, más abajo);
//   2. REDIRIGE A INICIO cualquier ruta que no esté acá (el efecto que compara
//      contra `location.pathname`, también más abajo).
//
// Por (1) alcanza con poner la sección. Por (2) hace falta que esté toda ruta a la
// que esa sección lleve, porque la comparación es `pathname === href` o
// `pathname.startsWith(href + '/')` y nada más.
//
// Cuando se abrió Candidaturas para alianzas se agregó `/apoderado/panel` —el ítem
// del menú— pero no las rutas que ese panel abre. Resultado: el apoderado entraba al
// panel, apretaba para cargar la candidatura y el efecto lo devolvía a Inicio antes
// de que pudiera tocar el formulario. `/apoderado/panel` no cubre
// `/apoderado/candidatura/...`: son hermanas, no una prefijo de la otra.
//
// El prefijo `/apoderado/candidatura` cubre el formulario (`/:id`), la única ruta
// de ese prefijo montada DENTRO del layout.
// subsanación vive fuera y por eso nunca pasa por este control.
//
// Regla para la próxima ruta que se agregue: si es una pantalla autenticada, va
// dentro del layout (criterio de la Secretaría: los wizards con usuario van CON
// panel lateral; sólo los trámites externos sin usuario van a pantalla completa), y
// entonces SU RUTA tiene que estar en esta lista o va a rebotar a Inicio.
//
// TABS_ALIANZA_VIGENTE se usa siempre para quien administra una alianza (ver
// tabsPermitidasExternas y el efecto de redirección, más abajo): ya no depende
// de si la alianza está vigente, solo de si administra una alianza.
const TABS_ALIANZA_VIGENTE = [...TABS_ALIANZA, '/apoderado/panel', '/apoderado/candidatura'];

const icons = {
  home: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  folder: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  ),
  building: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  dollar: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  check: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  chart: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  inbox: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  ),
  users: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  document: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  settings: (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  menu: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  close: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  chevronLeft: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  ),
  chevronRight: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ),
};

interface GroupItemDef {
  name: string;
  href: string;
  roles?: string[];
  // Marca activo SOLO con la ruta exacta. Hace falta cuando otro ítem del menú
  // cuelga por debajo: /gestion/alianzas empieza con /gestion, así que sin esto
  // los dos se pintaban activos al mismo tiempo.
  exacta?: boolean;
}

interface GroupDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: GroupItemDef[];
}

const INTERNAL_GROUPS: GroupDef[] = [
  {
    id: 'expedientes',
    label: 'Expedientes',
    icon: icons.folder,
    items: [
      { name: 'Bandeja de trabajo', href: '/gestion' },
      // BANDEJA DE ALIANZAS — OCULTA A PEDIDO (2026-08-25)
      // La página existe y la ruta sigue viva en /gestion/alianzas: sólo se sacó
      // del menú. Para volver a mostrarla, descomentar esta línea y volver a poner
      // `exacta: true` en 'Bandeja de trabajo' (sin eso, /gestion queda marcado como
      // activo al entrar a /gestion/alianzas, porque es prefijo suyo).
      // { name: 'Alianzas', href: '/gestion/alianzas', },
      { name: 'Listado', href: '/expedientes' },
      { name: 'Documentos', href: '/documentos' },
    ],
  },
  {
    id: 'partidos',
    label: 'Partidos',
    icon: icons.building,
    items: [
      { name: 'Partidos', href: '/partidos' },
      { name: 'Autoridades', href: '/autoridades' },
      { name: 'Afiliados', href: '/afiliados' },
      { name: 'Padrones', href: '/padrones', roles: ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ANALISTA'] },
      { name: 'Control Duplicados', href: '/afiliados-control', roles: ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUDITOR', 'MESA_ENTRADAS'] },
      { name: 'Adherentes', href: '/adherentes', roles: ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUDITOR', 'MESA_ENTRADAS'] },
      ...(AUTENTICIDAD_HABILITADA
        ? [
          { name: 'Lotes de afiliación', href: '/lotes-afiliacion-sep', roles: ROLES_INTERNOS },
          { name: 'Verificación de autenticidad', href: '/verificacion-autenticidad', roles: ROLES_INTERNOS },
        ]
        : []),
    ],
  },
  {
    id: 'candidaturas',
    label: 'Candidaturas',
    icon: icons.check,
    items: [
      { name: 'Bandeja de expedientes', href: '/candidaturas/gaiman/bandeja', roles: ['ANALISTA', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ADMINISTRADOR', 'MESA_ENTRADAS'] },
      { name: 'Agrupaciones habilitadas', href: '/gaiman/agrupaciones-habilitadas', roles: ['SUPERVISOR', 'ADMINISTRADOR'] },
    ],
  },
];

const GRUPO_AFILIADOS_EXTERNO: GroupDef = {
  id: 'afiliados-externo',
  label: 'Afiliados',
  icon: icons.users,
  items: [
    { name: '1 · Lotes de afiliación', href: '/lotes-afiliacion' },
    { name: '2 · Afiliados registrados', href: '/afiliados', exacta: true },
  ],
};

const navigationExternal = [
  { name: 'Inicio', href: '/dashboard', icon: icons.home, roles: undefined as string[] | undefined },
  { name: 'Mi Partido', href: '/mi-partido', icon: icons.building, roles: undefined as string[] | undefined },
  { name: 'Autoridades', href: '/autoridades', icon: icons.users, roles: undefined as string[] | undefined },
  { name: 'Afiliados', href: '/afiliados', icon: icons.users, roles: undefined as string[] | undefined },
  { name: 'Expedientes', href: '/expedientes', icon: icons.folder, roles: undefined as string[] | undefined },
  { name: 'Documentos', href: '/documentos', icon: icons.document, roles: undefined as string[] | undefined },
  ...(CONSULTAS_HABILITADAS
    ? [{ name: 'Consultas a la SEP', href: '/consultas', icon: icons.inbox, roles: ROLES_CONSULTAS_PARTIDO as string[] | undefined }]
    : []),
  { name: 'Financiamiento', href: '/financiamiento', icon: icons.dollar, roles: ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE'] },
  { name: 'Usuarios', href: '/usuarios', icon: icons.users, roles: ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE'] },
  { name: 'Candidaturas', href: '/apoderado/panel', icon: icons.check, roles: ['APODERADO_FIRMANTE'] },
];

export default function MainLayout() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { collapsed, toggleCollapsed, openGroups, toggleGroup, openGroup } = useSidebarState();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const recon = useReconocimientoApoderado();
  const alianza = useAlianzaPartido();
  const agrupaciones = useAgrupaciones();

  const isInternal = ROLES_INTERNOS.includes(user?.rol || '');
  // Consultas que esperan respuesta de la SEP: el número junto a "Consultas de partidos".
  const consultasPendientes = useConsultasPendientes(isInternal);
  // Consultas con novedades de la SEP que el partido todavía no vio: el número junto a "Consultas a la SEP".
  const consultasPendientesPartido = useConsultasPendientesPartido();

  // Restricción de secciones para el apoderado de un reconocimiento en formación
  // (null = sin restricción, navegación externa completa).
  const tabsPermitidasExternas: string[] | null = recon.enEtapaAutoridades || recon.enTramiteDefinitivo
    // Mientras el trámite está en el Secretario o en el TEP se mantiene la misma
    // navegación acotada de la etapa de autoridades: el partido no tiene acciones
    // nuevas, y devolverle de golpe la navegación completa hacía parecer que el
    // trámite ya había terminado.
    ? TABS_RECON_AUTORIDADES
    : recon.enEtapaAfiliados
    ? TABS_RECON_AFILIADOS
    : recon.esReconocimientoEnFormacion
      ? recon.mostrarWizard
        ? TABS_RECON_WIZARD_PENDIENTE
        : recon.documentacionCargada
          ? TABS_RECON_DOC_ENVIADA
          : TABS_RECON_EN_FORMACION
    // Va al final de la cadena: un partido en formación no puede integrar una
    // alianza (para eso tiene que estar vigente), así que los dos casos no se
    // pisan; si alguna vez se pisaran, manda el reconocimiento.
    : alianza.gestionaAlianza
      ? TABS_ALIANZA_VIGENTE
      : null;

  // Filtra sub-ítems por rol y descarta grupos vacíos
  const visibleGroups = INTERNAL_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => !item.roles || item.roles.includes(user?.rol || '')),
  })).filter(group => group.items.length > 0);

  // Abre automáticamente el grupo que contiene la ruta activa
  useEffect(() => {
    for (const group of INTERNAL_GROUPS) {
      const hasActive = group.items.some(
        item => location.pathname === item.href || location.pathname.startsWith(item.href + '/')
      );
      if (hasActive) {
        openGroup(group.id);
        break;
      }
    }
  }, [location.pathname, openGroup]);

  // Mi Partido y Financiamiento no existen para el partido hasta que tenga el
  // reconocimiento DEFINITIVO. Ocultar el link del menú no alcanzaba: entrando por
  // URL las páginas se abrían igual. Acá se redirige al inicio.
  useEffect(() => {
    const bloqueadas = [
      ...(recon.restringirSeccionesVigente ? SECCIONES_SOLO_VIGENTE : []),
      ...(recon.restringirFinanciamiento ? SECCIONES_SOLO_VIGENTE_FINANCIAMIENTO : []),
    ];
    if (bloqueadas.length === 0) return;
    const bloqueada = bloqueadas.some(
      href => location.pathname === href || location.pathname.startsWith(href + '/'),
    );
    if (bloqueada) navigate('/dashboard', { replace: true });
  }, [recon.restringirSeccionesVigente, recon.restringirFinanciamiento, location.pathname, navigate]);

  // Usuario de una alianza: ocultar los links del menú no alcanza, entrando por URL
  // las páginas se abren igual (mismo motivo que la redirección de
  // SECCIONES_SOLO_VIGENTE de arriba). Acá se redirige al inicio.
  //
  // Se espera a que el hook resuelva: mientras carga, `gestionaAlianza` es false, y
  // redirigir en ese lapso sacaría de la página a un usuario que sí puede estar.
  useEffect(() => {
    if (isInternal || alianza.isLoading || !alianza.gestionaAlianza) return;
    const permitida = TABS_ALIANZA_VIGENTE.some(
      href => location.pathname === href || location.pathname.startsWith(href + '/'),
    );
    if (!permitida) navigate('/dashboard', { replace: true });
  }, [
    isInternal, alianza.isLoading, alianza.gestionaAlianza,
    location.pathname, navigate,
  ]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const rutaActiva = (href: string, exacta?: boolean) =>
    exacta
      ? location.pathname === href
      : location.pathname === href || location.pathname.startsWith(href + '/');

  const navItemClass = (href: string, isCollapsed: boolean, exacta?: boolean) =>
    `flex items-center gap-x-3 rounded-md p-3 text-sm font-medium transition ${
      isCollapsed ? 'justify-center' : ''
    } ${
      rutaActiva(href, exacta)
        ? 'bg-primary-800 text-white'
        : 'text-primary-100 hover:bg-primary-600 hover:text-white'
    }`;

  const renderInternalNav = (isCollapsed: boolean) => (
    <nav className="flex flex-1 flex-col gap-y-1">
      <Link to="/dashboard" title={isCollapsed ? 'Inicio' : undefined} className={navItemClass('/dashboard', isCollapsed)}>
        {icons.home}
        {!isCollapsed && <span>Inicio</span>}
      </Link>

      {visibleGroups.map(group => (
        <SidebarGroup
          key={group.id}
          id={group.id}
          label={group.label}
          icon={group.icon}
          items={group.items}
          isOpen={openGroups.includes(group.id)}
          collapsed={isCollapsed}
          onToggle={() => toggleGroup(group.id)}
        />
      ))}
      
      <Link to="/financiamiento" title={isCollapsed ? 'Financiamiento' : undefined} className={navItemClass('/financiamiento', isCollapsed)}>
        {icons.dollar}
        {!isCollapsed && <span>Financiamiento</span>}
      </Link>

      {['ADMINISTRADOR', 'AUDITOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '') && (
        <Link to="/reportes" title={isCollapsed ? 'Reportes' : undefined} className={navItemClass('/reportes', isCollapsed)}>
          {icons.chart}
          {!isCollapsed && <span>Reportes</span>}
        </Link>
      )}

      {/* Interacciones y consultas de partidos: un solo ítem; adentro se alternan
          con PestaniasComunicaciones. Activo en las dos rutas. Sin consultas
          habilitadas, es el ítem de Interacciones de siempre. */}
      <Link
        to={DESTINO_COMUNICACIONES}
        title={isCollapsed ? `${ETIQUETA_COMUNICACIONES}${consultasPendientes ? ` (${consultasPendientes})` : ''}` : undefined}
        className={navItemClass(rutaActiva('/gestion-consultas') ? '/gestion-consultas' : '/interacciones', isCollapsed)}
      >
        {icons.inbox}
        {!isCollapsed && (
          <>
            <span className="flex-1">{ETIQUETA_COMUNICACIONES}</span>
            {consultasPendientes > 0 && (
              <span className="text-xs bg-primary-900 text-primary-200 px-1.5 py-0.5 rounded" title="Consultas que esperan respuesta de la SEP">
                {consultasPendientes}
              </span>
            )}
          </>
        )}
      </Link>

      <div className="my-2 border-t border-primary-600" />

      <Link to="/usuarios" title={isCollapsed ? 'Usuarios' : undefined} className={navItemClass('/usuarios', isCollapsed)}>
        {icons.users}
        {!isCollapsed && <span>Usuarios</span>}
      </Link>

      {user?.rol === 'ADMINISTRADOR' && (
        <Link to="/configuracion" title={isCollapsed ? 'Configuración' : undefined} className={navItemClass('/configuracion', isCollapsed)}>
          {icons.settings}
          {!isCollapsed && (
            <>
              <span className="flex-1">Configuración</span>
              <span className="text-xs bg-primary-900 text-primary-200 px-1.5 py-0.5 rounded">admin</span>
            </>
          )}
        </Link>
      )}
    </nav>
  );

  const renderExternalNav = (isCollapsed: boolean) => (
    <nav className="flex flex-1 flex-col gap-y-1">
      {navigationExternal
        .filter(item => !item.roles || item.roles.includes(user?.rol || ''))
        // Reconocimiento en formación: solo el subconjunto de secciones del momento.
        .filter(item => !tabsPermitidasExternas || tabsPermitidasExternas.includes(item.href))
        // Mi Partido y Financiamiento: sólo con el reconocimiento definitivo
        // (partido/expediente VIGENTE).
        .filter(item => !(recon.restringirSeccionesVigente && SECCIONES_SOLO_VIGENTE.includes(item.href)))
        // Financiamiento, además, tampoco en reempadronamiento sin partido VIGENTE.
        .filter(item => !(recon.restringirFinanciamiento && SECCIONES_SOLO_VIGENTE_FINANCIAMIENTO.includes(item.href)))
        .map(item =>
          item.href === '/afiliados' && AUTENTICIDAD_HABILITADA ? (
            <SidebarGroup
              key={GRUPO_AFILIADOS_EXTERNO.id}
              {...GRUPO_AFILIADOS_EXTERNO}
              isOpen={
                openGroups.includes(GRUPO_AFILIADOS_EXTERNO.id) ||
                GRUPO_AFILIADOS_EXTERNO.items.some(i => rutaActiva(i.href, i.exacta))
              }
              collapsed={isCollapsed}
              onToggle={() => toggleGroup(GRUPO_AFILIADOS_EXTERNO.id)}
            />
          ) : (
            <Link
              key={item.href}
              to={item.href}
              title={isCollapsed ? item.name : undefined}
              className={`flex items-center gap-x-3 rounded-md p-3 text-sm font-medium transition ${
                isCollapsed ? 'justify-center' : ''
              } ${
                location.pathname === item.href
                  ? 'bg-primary-800 text-white'
                  : 'text-primary-100 hover:bg-primary-600 hover:text-white'
              }`}
            >
              {item.icon}
              {!isCollapsed && (
                item.href === '/consultas' && consultasPendientesPartido > 0 ? (
                  <>
                    <span className="flex-1">{item.name}</span>
                    <span className="text-xs bg-primary-900 text-primary-200 px-1.5 py-0.5 rounded" title="Consultas con novedades de la SEP">
                      {consultasPendientesPartido}
                    </span>
                  </>
                ) : item.name
              )}
            </Link>
          ),
        )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar móvil */}
      <Transition.Root show={mobileSidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
              <div className="flex grow flex-col overflow-y-auto scrollbar-hide bg-primary-700 px-3 pb-4">
                  <div className="flex h-16 shrink-0 items-center justify-between">
                    <span className="text-white text-xl font-bold">SIREPPAR</span>
                    <button onClick={() => setMobileSidebarOpen(false)} className="text-primary-200 hover:text-white p-1">
                      {icons.close}
                    </button>
                  </div>
                  {isInternal ? renderInternalNav(false) : renderExternalNav(false)}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

            {/* Sidebar desktop */}
            <div
        className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-200 ${
          collapsed ? 'lg:w-14' : 'lg:w-[232px]'
        }`}
      >
        <div className="flex grow flex-col overflow-hidden bg-primary-700 pb-4">
          <div className="flex h-16 shrink-0 items-center px-3 border-b border-primary-600">
            {!collapsed && (
              <span className="flex-1 text-white text-base font-bold tracking-wide">SIREPPAR</span>
            )}
            <button
              onClick={toggleCollapsed}
              title={collapsed ? 'Expandir (Ctrl+B)' : 'Colapsar (Ctrl+B)'}
              className={`flex items-center justify-center p-1.5 rounded-md text-primary-200 hover:text-white hover:bg-primary-600 transition ${
                collapsed ? 'mx-auto' : 'ml-auto'
              }`}
            >
              {collapsed ? icons.chevronRight : icons.chevronLeft}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-hide px-2 pt-3">
            {isInternal ? renderInternalNav(collapsed) : renderExternalNav(collapsed)}
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className={`overflow-x-hidden transition-all duration-200 ${collapsed ? 'lg:pl-14' : 'lg:pl-[232px]'}`}>
        {/* Header */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setMobileSidebarOpen(true)}
          >
            {icons.menu}
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <CampanaAlertas />
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-x-3 p-1.5 rounded-lg hover:bg-gray-100 transition">
                  <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                    {user?.nombre?.[0]}{user?.apellido?.[0]}
                  </div>
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-medium text-gray-900">{user?.nombre} {user?.apellido}</p>
                    {/* Con más de una agrupación, el subtítulo dice CON CUÁL estás
                        trabajando: es el dato que cambia lo que ves en todo el sistema,
                        y sin él no habría forma de notar que estás en la equivocada. */}
                    <p className="text-xs text-gray-500">
                      {agrupaciones.puedeCambiar && agrupaciones.activa
                        ? agrupaciones.activa.denominacion
                        : user?.rol?.replace(/_/g, ' ')}
                    </p>
                  </div>
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {/* Selector de agrupación. Sólo aparece si hay más de una: la
                        enorme mayoría de los usuarios tiene sólo su partido y no
                        tienen que ver una sección que no les dice nada. */}
                    {agrupaciones.puedeCambiar && (
                      <>
                        <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Estás administrando
                        </p>
                        {agrupaciones.agrupaciones.map(ag => (
                          <Menu.Item key={ag.partidoId}>
                            {({ active }) => (
                              <button
                                onClick={() => !ag.activa && agrupaciones.cambiarA(ag.partidoId)}
                                disabled={ag.activa || agrupaciones.cambiando}
                                className={`flex w-full items-start gap-2 px-4 py-2 text-left text-sm ${
                                  active && !ag.activa ? 'bg-gray-100' : ''
                                } ${ag.activa ? 'cursor-default' : ''}`}
                              >
                                <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${
                                  ag.activa ? 'bg-blue-600' : 'bg-gray-300'
                                }`} />
                                <span className="min-w-0">
                                  <span className={`block truncate ${ag.activa ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                    {ag.denominacion}
                                  </span>
                                  <span className="block text-xs text-gray-500">
                                    {ag.clase === 'ALIANZA'
                                      ? 'Alianza'
                                      : ag.clase === 'CONFEDERACION'
                                        ? 'Confederación'
                                        : 'Partido'}
                                    {ag.activa ? ' · en uso' : ''}
                                  </span>
                                </span>
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                        <div className="my-1 border-t border-gray-100" />
                      </>
                    )}
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/perfil"
                          className={`block px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''} text-gray-700`}
                        >
                          Mi Perfil
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`block w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''} text-gray-700`}
                        >
                          Cerrar Sesión
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Contenido de la página */}
        <main className="py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
